import { useLayoutEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type {
  CreativeOverlayModel,
  CreativeStickerInstance,
} from "../lib/creative-render-state.js";
import type { StickerZone } from "../../game/emotion-model.js";
import {
  layoutSticker,
  transitionFlashOpacity,
  transitionSwipeProgress,
} from "../lib/creative-overlay-compose.js";
import {
  screenToStickerAnchorMilli,
  scaleStickerByFactor,
  clampStickerScaleMilli,
  clampStickerRotationMilli,
  clampStickerAnchorXMilli,
  clampStickerAnchorYMilli,
} from "../lib/sticker-edit.js";
import { StickerGlyph } from "./StickerGlyph.js";
import { CaptionOverlay } from "./CaptionOverlay.js";
import type { PlacementCue } from "../lib/caption-placement.js";

/**
 * Selection-aware sticker edit controller: unlimited instances, only the
 * `selectedId` one shows handles. Tap-to-select is idempotent (onSelect is
 * called on every pointerdown on a sticker body, selected or not).
 */
export interface StickerEditController {
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Live drag to reposition, then commit on release (per-mille anchors). */
  onMoveCommit: (id: string, anchorXMilli: number, anchorYMilli: number) => void;
  /** Commit a new clamped scale (milli) — used by two-finger pinch-resize. */
  onResizeCommit: (id: string, scaleMilli: number) => void;
  /** Commit a new clamped scale AND rotation together (one undo step) — used
   *  by the combined bottom-left resize+rotate handle. */
  onTransformCommit: (id: string, scaleMilli: number, rotationMilli: number) => void;
  /** Remove this sticker instance. */
  onDelete: (id: string) => void;
}

/**
 * Live editor overlay — DOM/SVG twin of Canvas2D `composeCreativeOverlay`.
 * Stickers use the same pure `layoutSticker` geometry as capture.
 * Z-order: stickers → transition → caption.
 */
export function CreativeOverlayLayer({
  overlay,
  captionDraggable = false,
  onCaptionMove,
  captionPlacementCue,
  onCycleCaptionStyle,
  onCaptionTransform,
  stickerEdit = null,
  stickerZones = [],
  showStickerZones = false,
  highlightZoneIds = [],
  playheadSourceTick = 0,
}: {
  overlay: CreativeOverlayModel;
  captionDraggable?: boolean;
  onCaptionMove?: (anchorXMilli: number, anchorYMilli: number) => void;
  captionPlacementCue?: PlacementCue | null;
  /** Cycle the caption STYLE prev (-1) / next (+1) — drives the inline on-video
   *  switcher; only meaningful (and only rendered) while the caption tool is
   *  active, i.e. whenever `captionDraggable` is true. */
  onCycleCaptionStyle?: (dir: -1 | 1) => void;
  /** Commit a new caption scale (milli) + rotation (millidegrees) together —
   *  driven by the caption's bottom-left combined resize+rotate handle.
   *  Presentation-only; only meaningful while the caption tool is active. */
  onCaptionTransform?: (scaleMilli: number, rotationMilli: number) => void;
  /** When set, every sticker is tappable-to-select; only `stickerEdit.selectedId`
   *  shows handles and is directly editable on the video. */
  stickerEdit?: StickerEditController | null;
  /** The scenario's sticker zones (finer than Focus subjects), for the
   *  discoverability hint drawn while the sticker tool is open. */
  stickerZones?: StickerZone[];
  /** Show a faint dashed labeled box for each zone — the sticker/Decorate
   *  tool being active. Purely a placement hint; never affects editing. */
  showStickerZones?: boolean;
  /** Zone ids that have an authored combo for the CURRENTLY SELECTED sticker
   *  id — drawn EMPHASIZED (brighter border/label) so the player sees "this
   *  sticker reacts here" while every other zone stays faint. */
  highlightZoneIds?: string[];
  /** Current playhead position in SOURCE ticks (same units as `StickerZone`'s
   *  resolved `startTick`/`endTick`). A zone is a BEAT-SCOPED marker: it only
   *  renders while the playhead sits inside its own beat window, so scrubbing
   *  reveals different zones per story moment. Never hints WHICH sticker a
   *  zone wants — marker only. */
  playheadSourceTick?: number;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ w: 360, h: 640 });
  const flash = transitionFlashOpacity(overlay.transition);
  const swipe = transitionSwipeProgress(overlay.transition);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = Math.max(1, Math.round(r.width));
      const h = Math.max(1, Math.round(r.height));
      setFrame((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="creative-overlay-root"
      data-testid="creative-overlay"
      aria-hidden
    >
      {showStickerZones && stickerZones.length > 0 ? (
        <div className="creative-sticker-zones" data-testid="creative-sticker-zones" aria-hidden>
          {stickerZones
            .filter((z) => playheadSourceTick >= z.startTick && playheadSourceTick < z.endTick)
            .map((z) => {
              const reacting = highlightZoneIds.includes(z.id);
              // Marker only — no text label. The zone is a "something works
              // here" hint; which sticker to use lives in "Ask a colleague".
              return (
                <div
                  key={z.id}
                  className={`creative-sticker-zone${reacting ? " is-reacting" : ""}`}
                  data-testid={`creative-sticker-zone-${z.id}`}
                  data-reacting={reacting ? "true" : undefined}
                  style={{
                    left: `${z.rect.x * 100}%`,
                    top: `${z.rect.y * 100}%`,
                    width: `${z.rect.w * 100}%`,
                    height: `${z.rect.h * 100}%`,
                  }}
                />
              );
            })}
        </div>
      ) : null}
      <div className="creative-stickers" data-testid="creative-stickers">
        {overlay.stickers.map((s) => (
          <StickerNode
            key={s.id}
            sticker={s}
            frameW={frame.w}
            frameH={frame.h}
            edit={stickerEdit ?? null}
          />
        ))}
      </div>
      {overlay.transition.kind === "flash" && flash > 0 ? (
        <div
          className="creative-transition flash"
          data-testid="creative-transition"
          data-kind="flash"
          data-phase={overlay.transition.phase.toFixed(3)}
          style={{ opacity: flash }}
        />
      ) : null}
      {overlay.transition.kind === "swipe" && swipe < 1 ? (
        <div
          className="creative-transition swipe"
          data-testid="creative-transition"
          data-kind="swipe"
          data-phase={overlay.transition.phase.toFixed(3)}
          style={{
            transform: `translateX(${swipe * 100}%)`,
          }}
        />
      ) : null}
      <CaptionOverlay
        caption={overlay.caption}
        draggable={captionDraggable}
        onMove={onCaptionMove}
        placementCue={captionPlacementCue}
        onCycleStyle={onCycleCaptionStyle}
        onTransform={onCaptionTransform}
      />
    </div>
  );
}

/** Screen-space distance (px) a pointer must travel from the press point
 *  before a body pointerdown turns into a move — below this, releasing is a
 *  TAP (select only, sticker stays put). */
const MOVE_THRESHOLD_PX = 4;

type LiveEdit =
  | {
      mode: "move";
      pointerId: number;
      rect: DOMRect;
      /** Press point, per-mille frame coords, minus the sticker's anchor at
       *  press time — added back to every subsequent pointer anchor so the
       *  sticker tracks the finger by DELTA instead of jumping to it. */
      grabOffsetXMilli: number;
      grabOffsetYMilli: number;
      startClientX: number;
      startClientY: number;
      /** Flips true once the pointer crosses `MOVE_THRESHOLD_PX`; stays false
       *  for a tap (no live anchor is shown and nothing commits on release). */
      moving: boolean;
      anchorXMilli: number;
      anchorYMilli: number;
    }
  | { mode: "pinch"; startDist: number; baseScaleMilli: number; scaleMilli: number }
  | {
      mode: "transform";
      pointerId: number;
      startDist: number;
      startAngle: number;
      baseScaleMilli: number;
      baseRotationMilli: number;
      scaleMilli: number;
      rotationMilli: number;
    };

function StickerNode({
  sticker,
  frameW,
  frameH,
  edit,
}: {
  sticker: CreativeStickerInstance;
  frameW: number;
  frameH: number;
  /** Selection-aware controller shared by every sticker; only the instance
   *  whose id matches `edit.selectedId` is `editable` (shows handles). */
  edit: StickerEditController | null;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<LiveEdit | null>(null);
  // Active pointers on the sticker body — two of them start a pinch resize.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  // Live override during a gesture so the sticker tracks the finger 1:1 before
  // the edit graph commit lands.
  const [live, setLive] = useState<
    {
      anchorXMilli?: number;
      anchorYMilli?: number;
      scaleMilli?: number;
      rotationMilli?: number;
    } | null
  >(null);

  const editable = Boolean(edit) && sticker.id === edit!.selectedId;
  const drawn: CreativeStickerInstance = live
    ? {
        ...sticker,
        x: live.anchorXMilli !== undefined ? live.anchorXMilli / 1000 : sticker.x,
        y: live.anchorYMilli !== undefined ? live.anchorYMilli / 1000 : sticker.y,
        scale: live.scaleMilli !== undefined ? live.scaleMilli / 1000 : sticker.scale,
        rotationDeg:
          live.rotationMilli !== undefined ? live.rotationMilli / 1000 : sticker.rotationDeg,
      }
    : sticker;
  const L = layoutSticker(drawn, frameW, frameH);

  const frameRect = (): DOMRect | null => {
    const parent = nodeRef.current?.parentElement;
    return parent ? parent.getBoundingClientRect() : null;
  };

  // Tap (or the start of a drag) on the sticker body: select it FIRST — this
  // is idempotent when already selected — then begin the move gesture, so a
  // tap selects and a grab-drag on an already-selected sticker moves it in
  // one motion. An unselected sticker's pointerdown selects it too; a small
  // move immediately after is fine.
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!edit) return;
    e.preventDefault();
    e.stopPropagation();
    edit.onSelect(sticker.id);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture unavailable (jsdom / synthetic events) — drag still works */
    }
    if (pointersRef.current.size >= 2) {
      // Second finger → pinch-resize; cancel any single-finger move.
      const pts = [...pointersRef.current.values()];
      const startDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
      const baseScaleMilli = Math.round(sticker.scale * 1000);
      dragRef.current = {
        mode: "pinch",
        startDist,
        baseScaleMilli,
        scaleMilli: baseScaleMilli,
      };
      return;
    }
    const rect = frameRect();
    if (!rect) return;
    // Record the grab offset (committed anchor − press point) instead of
    // jumping the sticker to the press point: a tap must leave it exactly
    // where it was, and a drag must track the finger by delta. No `setLive`
    // here — the sticker stays at its committed position until the pointer
    // actually crosses the move threshold (see `onPointerMove`).
    const currentAnchorXMilli = Math.round(sticker.x * 1000);
    const currentAnchorYMilli = Math.round(sticker.y * 1000);
    const pressAnchor = screenToStickerAnchorMilli(e.clientX, e.clientY, rect);
    dragRef.current = {
      mode: "move",
      pointerId: e.pointerId,
      rect,
      grabOffsetXMilli: pressAnchor ? currentAnchorXMilli - pressAnchor.anchorXMilli : 0,
      grabOffsetYMilli: pressAnchor ? currentAnchorYMilli - pressAnchor.anchorYMilli : 0,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moving: false,
      anchorXMilli: currentAnchorXMilli,
      anchorYMilli: currentAnchorYMilli,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (drag.mode === "pinch" && pointersRef.current.size >= 2) {
      e.preventDefault();
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
      const scaleMilli = scaleStickerByFactor(drag.baseScaleMilli, dist / drag.startDist);
      drag.scaleMilli = scaleMilli;
      setLive({ scaleMilli });
      return;
    }
    if (drag.mode === "move" && drag.pointerId === e.pointerId) {
      e.preventDefault();
      if (!drag.moving) {
        const dx = e.clientX - drag.startClientX;
        const dy = e.clientY - drag.startClientY;
        if (Math.hypot(dx, dy) < MOVE_THRESHOLD_PX) return; // still within tap range
        drag.moving = true;
      }
      const pointerAnchor = screenToStickerAnchorMilli(e.clientX, e.clientY, drag.rect);
      if (!pointerAnchor) return;
      // Track the finger by DELTA: press-time grab offset is added back onto
      // every subsequent pointer anchor so the sticker never jumps.
      const anchorXMilli = clampStickerAnchorXMilli(
        pointerAnchor.anchorXMilli + drag.grabOffsetXMilli,
      );
      const anchorYMilli = clampStickerAnchorYMilli(
        pointerAnchor.anchorYMilli + drag.grabOffsetYMilli,
      );
      drag.anchorXMilli = anchorXMilli;
      drag.anchorYMilli = anchorYMilli;
      setLive({ anchorXMilli, anchorYMilli });
    }
  };

  const endGesture = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    pointersRef.current.delete(e.pointerId);
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* no active capture */
    }
    if (!drag || !edit) return;
    // Pinch end only once fewer than two fingers remain.
    if (drag.mode === "pinch" && pointersRef.current.size >= 1) {
      return;
    }
    dragRef.current = null;
    setLive(null);
    if (drag.mode === "move") {
      // A tap (never crossed the move threshold) selects only — commit
      // nothing, the anchor is unchanged. Only an actual drag commits.
      if (!drag.moving) return;
      edit.onMoveCommit(sticker.id, drag.anchorXMilli, drag.anchorYMilli);
    } else if (drag.mode === "transform") {
      edit.onTransformCommit(
        sticker.id,
        clampStickerScaleMilli(drag.scaleMilli),
        clampStickerRotationMilli(drag.rotationMilli),
      );
    } else {
      edit.onResizeCommit(sticker.id, clampStickerScaleMilli(drag.scaleMilli));
    }
  };

  // Combined bottom-left transform handle: distance from the sticker centre
  // to the pointer drives the scale factor, angle from the centre drives the
  // rotation — both previewed live and committed together on release.
  const beginHandleTransform = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!edit) return;
    e.preventDefault();
    e.stopPropagation();
    const cx = L.cx;
    const cy = L.cy;
    const rect = frameRect();
    const px = rect ? e.clientX - rect.left : e.clientX;
    const py = rect ? e.clientY - rect.top : e.clientY;
    const startDist = Math.hypot(px - cx, py - cy) || 1;
    const startAngle = Math.atan2(py - cy, px - cx);
    const baseScaleMilli = Math.round(sticker.scale * 1000);
    const baseRotationMilli = Math.round(sticker.rotationDeg * 1000);
    dragRef.current = {
      mode: "transform",
      pointerId: e.pointerId,
      startDist,
      startAngle,
      baseScaleMilli,
      baseRotationMilli,
      scaleMilli: baseScaleMilli,
      rotationMilli: baseRotationMilli,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable */
    }
  };

  const onHandleTransformMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.mode !== "transform" || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    const rect = frameRect();
    const px = rect ? e.clientX - rect.left : e.clientX;
    const py = rect ? e.clientY - rect.top : e.clientY;
    const dist = Math.hypot(px - L.cx, py - L.cy) || 1;
    const currentAngle = Math.atan2(py - L.cy, px - L.cx);
    const scaleMilli = scaleStickerByFactor(drag.baseScaleMilli, dist / drag.startDist);
    const deltaDeg = ((currentAngle - drag.startAngle) * 180) / Math.PI;
    const rotationMilli = clampStickerRotationMilli(
      drag.baseRotationMilli + Math.round(deltaDeg * 1000),
    );
    drag.scaleMilli = scaleMilli;
    drag.rotationMilli = rotationMilli;
    setLive({ scaleMilli, rotationMilli });
  };

  return (
    <div
      ref={nodeRef}
      className={`creative-sticker${edit ? " is-interactive" : ""}${editable ? " is-editable" : ""}${live ? " is-dragging" : ""}`}
      data-testid={`creative-sticker-${sticker.id}`}
      data-shape={L.shape}
      data-id={sticker.id}
      data-editable={editable ? "true" : undefined}
      data-cx={L.cx.toFixed(2)}
      data-cy={L.cy.toFixed(2)}
      data-w={L.width.toFixed(2)}
      data-h={L.height.toFixed(2)}
      data-rot={L.rotationDeg.toFixed(2)}
      style={{
        left: L.cx,
        top: L.cy,
        width: L.width,
        height: L.height,
        color: L.color,
        zIndex: sticker.zIndex,
        fontSize: L.fontSize,
        transform: `translate(-50%, -50%) rotate(${L.rotationDeg}deg)`,
        // Every sticker accepts tap-to-select while the sticker tool is open
        // (`edit` is the shared controller, non-null for ALL nodes — not just
        // the selected one). The overlay root defaults pointer-events to
        // `none` (desk.css `.creative-overlay-root`) so it never blocks video
        // taps when no tool needs it; without this explicit `auto` a
        // deselected sticker inherits that `none` and becomes untappable —
        // reselecting it would be impossible. Set inline (not just via the
        // `.is-interactive` CSS class) so this is authoritative regardless of
        // stylesheet load order/specificity, and directly assertable in tests.
        pointerEvents: edit ? "auto" : "none",
      }}
      onPointerDown={edit ? onPointerDown : undefined}
      onPointerMove={edit ? onPointerMove : undefined}
      onPointerUp={edit ? endGesture : undefined}
      onPointerCancel={edit ? endGesture : undefined}
    >
      <StickerGlyph
        shape={L.shape}
        text={L.text}
        color={L.color}
        fontSize={L.fontSize}
        src={sticker.artSrc}
      />
      {editable && edit ? (
        <>
          {/* Top-right × delete — the on-video handle for removing this
              instance; the panel's "Delete sticker" button is gone (delete
              lives ONLY here now). Body drag = move; the bottom-left handle
              resizes + rotates together. */}
          <button
            type="button"
            className="creative-sticker-delete"
            data-testid={`creative-sticker-delete-${sticker.id}`}
            aria-label="Delete sticker"
            title="Delete sticker"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              edit.onDelete(sticker.id);
            }}
          >
            ×
          </button>
          <span
            className="creative-sticker-transform"
            data-testid={`creative-sticker-transform-${sticker.id}`}
            role="slider"
            aria-label="Resize and rotate sticker"
            aria-hidden={false}
            onPointerDown={beginHandleTransform}
            onPointerMove={onHandleTransformMove}
            onPointerUp={endGesture}
            onPointerCancel={endGesture}
          />
        </>
      ) : null}
    </div>
  );
}

// StickerGlyph is now the shared component in ./StickerGlyph (used here + by the
// DecorateTool picker cards, so the preview always matches the on-video render).
